/**
@format
@flow
*/
import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { CroppedArea, CroppedObject } from '@ziynx/flow-types/media';

type Style = {
  containerStyle?: {
    height?: string,
    width?: string,
    ...
  },
  cropAreaStyle?: {
    opacity?: number,
    boxShadow?: string,
    ...
  },
  ...
}

type Props = $ReadOnly<{|
  /**
   * If set to true, grid will be shown
   * Defaults to true
   */
  gridlines?: boolean,
  /**
   * Source for image or vid as URL
   */
  source: string,
  /**
   * Image or video variant
   * Default image
   */
  variant: 'image' | 'video',
  /**
   * Determine if picture or video should fit
   * React-easy-crop prop
   * Defaults to contain
   */
  objectFit?: 'horizontal-cover' | 'vertical-cover' | 'contain',
  /**
   * Zoom
   */
  zoom?: number,
  /**
   * Aspect ration for image
   * Defaults to 16 /9
   */
  aspectRatio?: number,
  /**
   * Custom styles
   */
  style?: Style,
  /**
   * Custom onCropComplete fired when user stops moving media in cropper
   */
  onCropComplete?: (CroppedObject) => void,
  /**
   * Custom function to handle onZoom when user moves slider for zoom functions
   */
  onZoomChange?: () => void,
|}>;

const DEFAULT_STYLES = {
  containerStyle: {
    height: '508px',
    width: '904px',
  },
  cropAreaStyle: {
    opacity: 1,
    boxShadow: '0 0 0 999em rgba(245, 246, 250, 0.8)',
  },
};

/**
 * Cropper for image or video
 */
const MediaCropper = (props: Props): React$Node => {
  const {
    source,
    variant,
    aspectRatio,
    gridlines,
    onCropComplete,
    onZoomChange,
    zoom,
    objectFit,
    style: customStyles,
    ...rest
  } = props;

  const [crop, setCrop] = useState({ x: 0, y: 0 });

  const handleCropComplete = useCallback(
    (croppedArea: CroppedArea, croppedAreaPixels: CroppedArea) => {
      const result = { croppedArea, croppedAreaPixels };
      if (onCropComplete) onCropComplete(result);
    }, [],
  );

  return (
    <Cropper
      {...(variant === 'video' ? { video: source } : { image: source })}
      showGrid={gridlines}
      crop={crop}
      zoom={zoom}
      aspect={aspectRatio}
      onCropChange={setCrop}
      onZoomChange={onZoomChange}
      onCropComplete={handleCropComplete}
      style={{ ...DEFAULT_STYLES, ...customStyles }}
      objectFit={objectFit}
      {...rest}
    />
  );
};

export default MediaCropper;

MediaCropper.defaultProps = {
  gridlines: true,
  aspectRatio: 1 / 1,
  objectFit: 'horizontal-cover',
};
